/**
 *  fetch-shield.js
 *  --------------------------------------------------------------
 *  Some browser extensions (password managers, page translators,
 *  network-debug tools) monkey-patch `window.fetch` and read the
 *  Response body BEFORE the page can.  When the page then calls
 *  `.json()` it explodes with "TypeError: Failed to execute 'json'
 *  on 'Response': body stream already read".
 *
 *  This wrapper installs at app start, intercepts every `window.fetch`
 *  call, immediately reads + buffers the body via `.clone().text()`,
 *  and returns a fresh `Response` constructed from the buffered text.
 *  Every downstream `.json()` / `.text()` on the page's side now
 *  reads the buffered copy — the extension can do whatever it wants
 *  to the original.
 *
 *  Trade-offs:
 *  • Streamed responses are fully buffered → not suitable if you have
 *    SSE / chunked-streaming endpoints.  The Planner / Tools / Site
 *    have none, so this is fine.
 *  • Negligible CPU / memory cost for normal JSON payloads.
 *
 *  Install once, in `/app/frontend/src/index.js` BEFORE any React code
 *  runs.
 */

const ORIGINAL_FETCH = typeof window !== "undefined" && window.fetch
  ? window.fetch.bind(window)
  : null;

if (ORIGINAL_FETCH && !window.__hvFetchShieldInstalled) {
  window.__hvFetchShieldInstalled = true;

  window.fetch = async function shieldedFetch(input, init) {
    let raw;
    try {
      raw = await ORIGINAL_FETCH(input, init);
    } catch (err) {
      // Network / abort error — propagate as-is so callers' catch blocks fire.
      throw err;
    }

    // BINARY RESPONSES BYPASS THE TEXT-BUFFER PATH.
    // If we routed a PDF / image / octet-stream through `.text()` its bytes
    // would be decoded as UTF-8, every non-UTF-8 byte replaced with U+FFFD,
    // and the resulting Blob would be a corrupt file (Adobe Acrobat: "the
    // file is damaged and could not be repaired"). The shield only needs
    // to protect JSON endpoints from extension body drains, so we detect
    // known binary mime types up-front and return the raw Response instead.
    const contentType = (raw.headers && raw.headers.get && raw.headers.get("content-type")) || "";
    const isBinary =
      /^(?:application\/(?:pdf|octet-stream|zip|x-zip-compressed|vnd\.[^;]*|.*\+xml;?)?)/i.test(contentType) ||
      /^(?:image|audio|video)\//i.test(contentType) ||
      /^application\/(?:pdf|octet-stream|zip)/i.test(contentType);
    if (isBinary) {
      return raw;
    }

    // Pre-buffer the body so extensions can't drain it.
    let bodyText = "";
    try {
      bodyText = await raw.clone().text();
    } catch (_) {
      // If clone+read fails (e.g. extension already drained it), fall
      // back to the empty string — downstream `.json()` will return {}.
      bodyText = "";
    }

    // Build a fresh Response from the buffered text — has its own body
    // stream, so `.json()` / `.text()` on it work independently.
    const shielded = new Response(bodyText, {
      status: raw.status,
      statusText: raw.statusText,
      headers: raw.headers,
    });

    // Defensive `.json()` override — many endpoints in this app return
    // an empty body on 204 No Content, 401/403 errors from upstream
    // proxies, or a plain HTML 502 from a CDN failover. Without this
    // override every empty / non-JSON body crashes with
    //   "Failed to execute 'json' on 'Response': Unexpected end of
    //    JSON input"
    // even though the page would handle a falsy/empty object just fine.
    // We return `{}` for empty/whitespace and for unparseable bodies;
    // callers still get the real `r.status` and `r.ok` to branch on.
    const safeJson = async () => {
      const trimmed = (bodyText || "").trim();
      if (!trimmed) return {};
      try { return JSON.parse(trimmed); }
      catch (_) { return {}; }
    };
    Object.defineProperty(shielded, "json", { value: safeJson, configurable: true });

    return shielded;
  };
}
